import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Lock, MapPin, ChevronRight, Landmark } from "lucide-react";
import { useLang } from "@/i18n";
import { api } from "@/api";
import { SectionHeading } from "@/components/Reveal";
import { scrollToId } from "@/components/Navbar";

const Badge = ({ visibility, t }) =>
  visibility === "public" ? (
    <span data-testid="opp-badge-public" className="inline-flex items-center gap-1.5 px-3 py-1 text-[10px] font-mono uppercase tracking-[0.18em] rounded-full border border-emerald-500/40 text-emerald-400">
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />{t.opportunities.public}
    </span>
  ) : (
    <span data-testid="opp-badge-confidential" className="inline-flex items-center gap-1.5 px-3 py-1 text-[10px] font-mono uppercase tracking-[0.18em] rounded-full border border-amber-500/40 text-amber-400">
      <Lock size={10} />{t.opportunities.confidential}
    </span>
  );

export default function Opportunities() {
  const { lang, t } = useLang();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/opportunities").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  const shown = items.filter((o) => filter === "all" || o.visibility === filter);

  return (
    <section id="opportunities" className="py-24 lg:py-32 bg-obsidian" data-testid="opportunities-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <SectionHeading overline={t.opportunities.overline} title={t.opportunities.title} text={t.opportunities.text} testid="opportunities-title" />
          <div className="flex gap-2 shrink-0" data-testid="opportunity-filters">
            {[["all", t.opportunities.all], ["public", t.opportunities.public], ["confidential", t.opportunities.confidential]].map(([v, label]) => (
              <button
                key={v}
                data-testid={`opportunity-filter-${v}`}
                onClick={() => setFilter(v)}
                className={`px-5 py-2 text-xs font-mono uppercase tracking-[0.16em] rounded-full border transition-colors duration-300 ${filter === v ? "border-gold bg-gold/10 text-gold" : "border-slate-700 text-slate-400 hover:border-slate-500 hover:text-slate-200"}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="opportunities-grid">
          {shown.map((o, i) => (
            <motion.article
              key={o.id}
              layout
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: (i % 2) * 0.1, ease: [0.16, 1, 0.3, 1] }}
              data-testid={`opportunity-card-${o.id}`}
              className={`group relative border hairline rounded-sm bg-obsidian-surface p-8 transition-[border-color] duration-500 hover:border-gold/40 ${o.locked ? "overflow-hidden" : ""}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex flex-wrap items-center gap-3 text-[11px] font-mono uppercase tracking-[0.16em] text-slate-400">
                  <span className="inline-flex items-center gap-1.5"><MapPin size={11} className="text-gold/70" />{o.country}</span>
                  <span className="inline-flex items-center gap-1.5"><Landmark size={11} className="text-gold/70" />{o.sector}</span>
                </div>
                <Badge visibility={o.visibility} t={t} />
              </div>

              <h3 className="mt-5 font-serif text-xl sm:text-2xl font-medium text-slate-100 leading-snug">
                {lang === "fr" ? o.title_fr : o.title_en}
              </h3>
              <p className="mt-2 text-[11px] font-mono uppercase tracking-[0.18em] text-gold/70">
                {lang === "fr" ? o.type_fr : o.type_en}
              </p>

              {o.locked ? (
                <div className="mt-6" data-testid={`opportunity-locked-${o.id}`}>
                  <div className="relative select-none" aria-hidden="true">
                    <p className="text-sm text-slate-500 font-light leading-relaxed blur-[5px]">
                      {lang === "fr" ? o.title_fr : o.title_en} — {lang === "fr" ? o.type_fr : o.type_en}. {t.opportunities.lockedText} {t.opportunities.lockedText}
                    </p>
                  </div>
                  <div className="mt-5 flex items-center gap-4">
                    <span className="inline-flex items-center gap-2 text-xs text-amber-400/90"><Lock size={13} />{t.opportunities.locked}</span>
                  </div>
                  <p className="mt-2 text-xs text-slate-400 font-light">{t.opportunities.lockedText}</p>
                  <button
                    data-testid={`opportunity-request-access-${o.id}`}
                    onClick={() => scrollToId("contact")}
                    className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 border border-gold/50 text-gold text-xs font-mono uppercase tracking-[0.16em] rounded-full hover:bg-gold hover:text-obsidian transition-colors duration-300"
                  >
                    {t.opportunities.requestAccess}<ChevronRight size={13} />
                  </button>
                </div>
              ) : (
                <div className="mt-6">
                  <p className="text-sm sm:text-[15px] text-slate-400 font-light leading-relaxed">
                    {lang === "fr" ? o.description_fr : o.description_en}
                  </p>
                  <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
                    {o.amount && (
                      <div>
                        <p className="font-mono uppercase tracking-[0.16em] text-slate-500 mb-1">{t.opportunities.amount}</p>
                        <p className="text-gold-light font-medium">{o.amount}</p>
                      </div>
                    )}
                    <div>
                      <p className="font-mono uppercase tracking-[0.16em] text-slate-500 mb-1">{t.opportunities.profileSought}</p>
                      <p className="text-slate-300 font-light leading-relaxed">{lang === "fr" ? o.profile_fr : o.profile_en}</p>
                    </div>
                  </div>
                  <button
                    data-testid={`opportunity-explore-${o.id}`}
                    onClick={() => scrollToId("contact")}
                    className="mt-7 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.16em] text-gold hover:gap-3 transition-all duration-300"
                  >
                    {t.hero.ctaSecondary}<ChevronRight size={13} />
                  </button>
                </div>
              )}

              <span className="absolute bottom-0 left-0 h-[2px] w-0 bg-gold group-hover:w-full transition-[width] duration-700" />
            </motion.article>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1, delay: 0.3 }}
          className="mt-12 flex items-start gap-3 text-xs text-slate-400 font-light max-w-2xl"
          data-testid="opportunities-private-note"
        >
          <Lock size={13} className="mt-0.5 shrink-0 text-red-400/70" />
          {t.opportunities.privateNote}
        </motion.p>
      </div>
    </section>
  );
}
